// eslint-disable-next-line import/extensions
import { COUNTRY_CODE, CurrencyCode } from "consts";

import { PracticeSlug } from "models";

// Mirrors api/types/carbonProgram.ts — keep the two in sync

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PRACTICE_LEVEL = {
  field: "field",
  farm: "farm",
} as const;

export const ACCOUNTING_METHODOLOGY = {
  inventory: "inventory",
  intervention: "intervention",
} as const;

export const CROPS_METHODOLOGY = {
  KLIM: "klim",
  CFP: "cfp",
} as const;

export const BEEF_METHODOLOGY = {
  LFL: "lfl",
  CFP: "cfp",
} as const;

export const DAIRY_METHODOLOGY = {
  LFL: "lfl",
  CFP: "cfp",
} as const;

export const PORK_METHODOLOGY = {
  LFL: "lfl",
} as const;

export const ENROLLMENT_TYPE = {
  auto: "auto",
  user: "user",
} as const;

export const INCENTIVE_NAME = {
  biodiversity: "biodiversity",
  companionCrops: "companion_crops",
  conservationTillage: "conservation_tillage",
  catchCrop: "catch_crops",
  fourR: "four_r",
  mixedCatchCrops: "mixed_catch_crops",
  simpleCatchCrops: "simple_catch_crops",
  basicCatchCrops: "basic_catch_crops",
  diverseCatchCrops: "diverse_catch_crops",
  noTillage: "no_tillage",
  reducedTillage: "reduced_tillage",
  summerCoverCrops: "summer_cover_crops",
  winterCoverCrops: "winter_cover_crops",
  diverseWinterCoverCrops: "diverse_winter_cover_crops",
} as const;

export type PracticeLevel = (typeof PRACTICE_LEVEL)[keyof typeof PRACTICE_LEVEL];
export type AccountingMethodology = (typeof ACCOUNTING_METHODOLOGY)[keyof typeof ACCOUNTING_METHODOLOGY];
export type FieldEnrollmentType = (typeof ENROLLMENT_TYPE)[keyof typeof ENROLLMENT_TYPE];
export type SocModel = "RegionalValues" | "SoilPlus3" | "FieldSocModel";
export type RequirementLevel = "requiredForSaving" | "requiredForProgram" | "optional" | "optionalAlwaysShown";
export type IncentiveName = (typeof INCENTIVE_NAME)[keyof typeof INCENTIVE_NAME];
export type MonitoringPeriodType = "harvest" | "calendar";

export type CropsMethoodolgy = (typeof CROPS_METHODOLOGY)[keyof typeof CROPS_METHODOLOGY];
export type BeefMethodology = (typeof BEEF_METHODOLOGY)[keyof typeof BEEF_METHODOLOGY];
export type DairyMethoodolgy = (typeof DAIRY_METHODOLOGY)[keyof typeof DAIRY_METHODOLOGY];
export type PorkMethoodolgy = (typeof PORK_METHODOLOGY)[keyof typeof PORK_METHODOLOGY];

/** Practice-based incentive, always paid per hectare */
export interface Incentive {
  readonly amount: number;
  readonly name: IncentiveName;
  readonly elementIds?: string[];
  readonly exclusiveWith?: IncentiveName[];
  readonly requirementLevel?: RequirementLevel;
  readonly tillageDepthLimit?: number;
  readonly ploughingAreaLimit?: number;
  readonly speciesCountThreshold?: number;
}

export interface Practice {
  readonly slug: PracticeSlug;
  readonly required: boolean;
  readonly dependencies: PracticeSlug[];
  readonly incentives: Incentive[];
  readonly baselineYears: number;
  readonly enrollable: boolean;
}

export interface PayoutDetails {
  readonly pointPrice: number;
  readonly permanenceBufferPercentage?: number;
}

export interface PayoutDetailsPerCategory {
  readonly removals: PayoutDetails;
  readonly avoidances: PayoutDetails;
  readonly reductions: PayoutDetails;
}

export interface YearlyFormPayout {
  readonly [payoutPerYear: string]: number;
}

export interface ValidationRules {
  readonly attributeEntriesByDate?: boolean;
  readonly includeManagementFromSoilInitStart?: boolean;
  readonly includeCarriedOverCrops?: boolean;
  readonly exemptUpcomingYearFromDependencies?: boolean;
}

/**
 * On / off switches for each platform feature. How an enabled feature behaves lives in its paired config object:
 * `fieldManagement` => `CarbonProgram.fieldsManagement`, `footprinting` => `CarbonProgram.farmLevelFootprinting`
 */
export interface PlatformFeatures {
  readonly fieldManagement?: {
    readonly enabled?: boolean; // the All Fields View
    readonly wallet?: boolean; // the Wallet in the navigation and payouts in the inventory impact panel
    readonly outcomeBasedIncentives?: boolean; // only relevant for inventory
    readonly practiceBasedIncentives?: boolean; // only relevant for inventory
    readonly impactPanel?: boolean;
    readonly insightsImpactPanel?: boolean; // show the insights impact panel (SoilSight) instead of the default impact panels
    readonly submission?: boolean;
  };
  readonly footprinting?: {
    readonly enabled?: boolean;
    readonly results?: boolean;
    readonly beef?: boolean;
    readonly dairy?: boolean;
    readonly pork?: boolean;
    readonly payouts?: boolean;
  };
  readonly planning?: {
    readonly cropMixComposer?: boolean;
  };
}

export interface SocMethodology {
  readonly socModel: SocModel;
  readonly version: string; // Not used by PEAS yet
}

/** Program-specific submission window, each date in MM/DD format. A date that isn't set uses the platform default */
export interface SubmissionDates {
  readonly start?: string;
  readonly due?: string;
  readonly end?: string;
}

/** Configures fields management (all fields view) and planning */
export interface FieldsManagement {
  readonly startYear?: number;
  readonly programCrops?: string[];
  /**
   * auto => all fields enrolled by default
   * user => user has to click the enroll button (suntory)
   */
  readonly fieldEnrollmentType?: FieldEnrollmentType;
  readonly practices: Practice[];
  readonly methodology: SocMethodology;
  readonly reportingPeriod?: {
    readonly start: string;
    readonly end: string;
  };
  readonly submissionDates?: SubmissionDates;
  readonly monitoringPeriodType?: MonitoringPeriodType;
  readonly validationRules?: ValidationRules;
  readonly payouts?: PayoutDetailsPerCategory;
  /** `null` hides the potential / maximum payout UI */
  readonly maximumPayoutPerHa?: number | null;
  readonly projectArea?: {
    readonly cropsOfInterest: string[];
  };
  readonly requirePreciseTillageDepth?: boolean;
  readonly requireTillageSoilMixing?: boolean;
}

/** Configures footprinting */
export interface FarmLevelFootprinting {
  formSubmissionBonuses: {
    // Per-crop means the bonus is applied to each required crop form
    // All-crops means the bonus is applied when *all* crop forms are finished
    cropPayouts: "per-crop" | "all-crops";
    crop?: YearlyFormPayout;
    farm?: YearlyFormPayout;
    dairy?: YearlyFormPayout;
    beef?: YearlyFormPayout;
  };
  programCrops: string[]; // CropIds from practices_crops
  startYear: number;
  disabledYears?: number[];
  verificationStartYear?: number; // Dictates in which year verification for the footprinting data should start
  methodology?: {
    version?: string;
    crops?: CropsMethoodolgy;
    beef?: BeefMethodology;
    dairy?: DairyMethoodolgy;
    pork?: PorkMethoodolgy;
  };
  // Footprinting form step ids (FormStepId in the api) treated as if they do not exist
  excludedFormSteps?: string[];
}

export interface CarbonProgram {
  readonly id: string;
  readonly programName: string;
  readonly internalName?: string;
  readonly programSlug: string;
  readonly bannerImageUrl?: string;
  readonly accountingMethodology: AccountingMethodology;
  readonly partnerId: string | null;
  readonly country: COUNTRY_CODE;
  readonly currency: CurrencyCode;
  // The program has no T&C on purpose, so we let admins assign it without a contract.
  readonly noProgramTerms?: boolean;
  // Farms go through verification and must complete document tasks before submitting
  readonly verificationRequired?: boolean;
  readonly platformFeatures: PlatformFeatures;
  // Feature config objects are always present, even when the feature is switched off
  readonly fieldsManagement: FieldsManagement;
  readonly farmLevelFootprinting: FarmLevelFootprinting;
}

/** Admin listing response */
export type CarbonProgramWithFarmCount = CarbonProgram & { readonly farmCount: number };
